import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatPrice, resolveImg } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { customerStatusLabel } from "../lib/shipping";
import PackageList from "../components/PackageList";
import OrderAdjustments from "../components/OrderAdjustments";
import { isShortItem } from "../lib/orderMath";
import { mtDate } from "../lib/dates";

export default function Account() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (!user) return;
    api
      .get("/orders/me")
      .then((res) => setOrders(res.data))
      .catch(() => setOrders([]))
      .finally(() => setLoadingOrders(false));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16" data-testid="account-page">
      <p className="eyebrow mb-3">My account</p>
      <h1 className="font-serif text-5xl text-[var(--ink-forest)] tracking-tight">
        Hello, {user.name.split(" ")[0]}
      </h1>
      <p className="text-[var(--muted-fg)] mt-2">{user.email}</p>

      <h2 className="font-serif text-3xl text-[var(--ink-forest)] mt-12 mb-6">Your orders</h2>

      {loadingOrders ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-[var(--muted)] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-10 text-center">
          <p className="text-[var(--muted-fg)] mb-5">You haven&apos;t placed any orders yet.</p>
          <Link
            to="/shop"
            className="inline-block bg-[var(--terracotta)] text-white rounded-full px-7 py-3 text-sm font-semibold"
          >
            Start shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((order) => (
            <div key={order.id} className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6">
              <div className="flex justify-between flex-wrap gap-3">
                <div>
                  <p className="font-mono text-sm font-semibold text-[var(--ink-forest)]" data-testid={`order-${order.id}-number`}>
                    {order.order_number}
                  </p>
                  <p className="text-xs text-[var(--muted-fg)] mt-0.5">
                    {mtDate(order.created_at)}
                  </p>
                </div>
                <span className={`self-start px-3 py-1 rounded-full text-xs font-semibold ${statusColor(order.status)}`} data-testid={`order-${order.id}-status`}>
                  {customerStatusLabel(order.status)}
                </span>
                <p className="font-semibold text-[var(--ink-forest)]">{formatPrice(order.total)}</p>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                {order.items.slice(0, 4).map((item, i) => (
                  <img
                    key={i}
                    src={resolveImg(item.image_url)}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover bg-[var(--muted)]"
                  />
                ))}
                {order.items.length > 4 && (
                  <div className="w-12 h-12 rounded-lg bg-[var(--muted)] flex items-center justify-center text-xs text-[var(--muted-fg)]">
                    +{order.items.length - 4}
                  </div>
                )}
              </div>
              {order.items.some(isShortItem) && (
                <ul className="mt-3 text-xs text-[var(--terracotta)] space-y-0.5" data-testid={`order-${order.id}-short`}>
                  {order.items.filter(isShortItem).map((it, i) => (
                    <li key={i}>{it.name}: sending {it.can_send} of {it.quantity}</li>
                  ))}
                </ul>
              )}
              {(order.swaps?.length > 0 || order.refunds?.length > 0) && (
                <div className="mt-5">
                  <OrderAdjustments order={order} />
                </div>
              )}
              {order.shipments?.length > 0 && (
                <div className="mt-5">
                  <PackageList order={order} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function statusColor(status) {
  switch (status) {
    case "paid": return "bg-emerald-100 text-emerald-800";
    case "shipped":
    case "partially_shipped": return "bg-blue-100 text-blue-800";
    case "delivered": return "bg-emerald-200 text-emerald-900";
    case "cancelled":
    case "expired": return "bg-rose-100 text-rose-800";
    default: return "bg-amber-100 text-amber-900";
  }
}
